import { Link, NavLink, useNavigate } from 'react-router-dom'
import {
  Shield, Sun, Moon, LogIn, LogOut, Menu, X, Settings,
  Crown, LayoutDashboard, ChevronDown, ShieldCheck,
} from 'lucide-react'
import { useState, useMemo, useEffect, useRef, forwardRef } from 'react'
import { useAuth } from '../context/AuthProvider'
import { useTheme } from '../context/ThemeProvider'
import { getMyAvatarBlob, getPreferences } from '../api/client'
import AccountModal from './AccountModal'
import TierBadge from './TierBadge'

type NavItem = {
  to: string
  label: string
  icon: typeof Shield
  show: boolean
  end?: boolean
}

export default function Navbar() {
  const { user, loading, entitlements } = useAuth()
  const { theme, toggle } = useTheme()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalTab, setModalTab] = useState<'profile' | 'account' | 'security' | 'preferences'>('profile')
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null)
  const [navbarDisplay, setNavbarDisplay] = useState<'avatar' | 'initials' | 'name'>('avatar')
  const navigate = useNavigate()

  useEffect(() => {
    if (!user) { setAvatarUrl(null); return }
    let revokeUrl: string | undefined
    getMyAvatarBlob().then((blob) => {
      const url = URL.createObjectURL(blob)
      revokeUrl = url
      setAvatarUrl(url)
    }).catch(() => setAvatarUrl(null))
    getPreferences().then((p) => {
      if (p?.navbarDisplay) setNavbarDisplay(p.navbarDisplay as 'avatar' | 'initials' | 'name')
    }).catch(() => {})
    return () => { if (revokeUrl) URL.revokeObjectURL(revokeUrl) }
  }, [user, modalOpen])

  useEffect(() => {
    if (!menuOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [menuOpen])

  const openSettings = (tab: 'profile' | 'account' | 'security' | 'preferences') => {
    setModalTab(tab)
    setModalOpen(true)
    setMenuOpen(false)
  }

  const initials = useMemo(() => {
    if (!user) return '?'
    const fn = (user.firstName || '').trim()
    const ln = (user.lastName || '').trim()
    if (fn || ln) return ((fn[0] || '') + (ln[0] || '')).toUpperCase()
    return (user.username || user.email || '?')[0].toUpperCase()
  }, [user])

  const handleLogout = () => {
    // Navigate to the dedicated /signout page — it fires the API call as
    // fire-and-forget and redirects immediately via location.replace(), so the
    // UI can't hang on a slow backend or freeze on the previous page's state.
    setMenuOpen(false)
    navigate('/signout')
  }

  const navItems: NavItem[] = [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, show: Boolean(user) },
  ]

  return (
    <>
      <nav className="nav-surface sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex items-center h-14 gap-3">
            {/* Brand */}
            <Link
              to="/"
              className="flex items-center gap-2 pr-2 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--nav-ring)/0.6)]"
              aria-label="IDFY home"
            >
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-brand-600/10 text-brand-600 dark:bg-brand-400/10 dark:text-brand-300 ring-1 ring-inset ring-brand-600/20 dark:ring-brand-400/20">
                <Shield size={16} strokeWidth={2.25} />
              </span>
              <span className="hidden sm:inline text-[15px] font-semibold tracking-tight text-[rgb(var(--nav-fg))]">IDFY</span>
            </Link>

            {/* Divider */}
            <span className="hidden md:block h-5 w-px bg-[rgb(var(--nav-border))]" aria-hidden="true" />

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-0.5 flex-1 min-w-0 overflow-x-auto">
              {navItems.filter((n) => n.show).map((item) => {
                const Icon = item.icon
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) => `nav-link${isActive ? ' is-active' : ''}`}
                  >
                    <Icon size={15} strokeWidth={2} className="nav-link__icon" />
                    {item.label}
                  </NavLink>
                )
              })}
            </div>

            <div className="flex items-center gap-1 ml-auto">
              {/* Theme toggle */}
              <button
                type="button"
                onClick={toggle}
                className="nav-icon-btn"
                aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
                title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
              >
                {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
              </button>

              {/* User menu */}
              {loading ? (
                <div className="h-9 w-9 rounded-full bg-[rgb(var(--nav-hover-bg)/0.5)] animate-pulse" />
              ) : user ? (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setMenuOpen((o) => !o)}
                    className="nav-avatar-btn"
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    aria-label="Account menu"
                  >
                    {navbarDisplay === 'name' ? (
                      <span className="text-sm font-medium text-[rgb(var(--nav-fg))] px-2 truncate max-w-[10rem]">
                        {user.firstName || user.username}
                      </span>
                    ) : navbarDisplay === 'initials' ? (
                      <span className="w-7 h-7 rounded-full bg-brand-100 dark:bg-brand-900/50 flex items-center justify-center text-[11px] font-semibold text-brand-700 dark:text-brand-300 ring-1 ring-inset ring-brand-600/20 dark:ring-brand-400/20">
                        {initials}
                      </span>
                    ) : avatarUrl ? (
                      <span className="w-7 h-7 rounded-full overflow-hidden ring-1 ring-[rgb(var(--nav-border))]">
                        <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
                      </span>
                    ) : (
                      <span className="w-7 h-7 rounded-full bg-brand-100 dark:bg-brand-900/50 flex items-center justify-center text-[11px] font-semibold text-brand-700 dark:text-brand-300 ring-1 ring-inset ring-brand-600/20 dark:ring-brand-400/20">
                        {initials}
                      </span>
                    )}
                    <ChevronDown
                      size={14}
                      className={`text-[rgb(var(--nav-fg-muted))] transition-transform duration-150 ${menuOpen ? 'rotate-180' : ''}`}
                      aria-hidden="true"
                    />
                  </button>

                  {menuOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setMenuOpen(false)}
                        aria-hidden="true"
                      />
                      <UserMenu
                        user={user}
                        avatarUrl={avatarUrl}
                        initials={initials}
                        tier={entitlements?.categoryCode && entitlements?.tierCode
                          ? { category: entitlements.categoryCode, tier: entitlements.tierCode }
                          : null}
                        onSettings={() => openSettings('profile')}
                        onMembership={() => { setMenuOpen(false); navigate('/plan') }}
                        onSignOut={handleLogout}
                      />
                    </>
                  )}
                </div>
              ) : (
                <NavLink
                  to="/signin"
                  className={({ isActive }) => `nav-link${isActive ? ' is-active' : ''}`}
                >
                  <LogIn size={15} strokeWidth={2} className="nav-link__icon" />
                  Sign In
                </NavLink>
              )}

              {/* Mobile menu toggle */}
              <button
                type="button"
                onClick={() => setMobileOpen((o) => !o)}
                className="nav-icon-btn md:hidden"
                aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
                aria-expanded={mobileOpen}
              >
                {mobileOpen ? <X size={18} /> : <Menu size={18} />}
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          {mobileOpen && (
            <div className="md:hidden pb-3 border-t border-[rgb(var(--nav-border))] pt-2 flex flex-col gap-0.5">
              {navItems.filter((n) => n.show).map((item) => {
                const Icon = item.icon
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) => `nav-link w-full justify-start${isActive ? ' is-active' : ''}`}
                    onClick={() => setMobileOpen(false)}
                  >
                    <Icon size={15} strokeWidth={2} className="nav-link__icon" />
                    {item.label}
                  </NavLink>
                )
              })}
              {!user && (
                <NavLink
                  to="/signin"
                  className={({ isActive }) => `nav-link w-full justify-start${isActive ? ' is-active' : ''}`}
                  onClick={() => setMobileOpen(false)}
                >
                  <LogIn size={15} strokeWidth={2} className="nav-link__icon" />
                  Sign In
                </NavLink>
              )}
            </div>
          )}
        </div>
      </nav>

      <AccountModal isOpen={modalOpen} initialTab={modalTab} onClose={() => setModalOpen(false)} />
    </>
  )
}

/* ──────────────────────────────────────────────────────────
 * User menu dropdown
 * Rich identity header, grouped items, arrow-key navigation.
 * ────────────────────────────────────────────────────────── */

type MenuUser = {
  firstName?: string
  lastName?: string
  username?: string
  email?: string
  twoFactorEnabled?: boolean
}

function UserMenu({
  user, avatarUrl, initials, tier, onSettings, onMembership, onSignOut,
}: {
  user: MenuUser
  avatarUrl: string | null
  initials: string
  tier: { category: string; tier: string } | null
  onSettings: () => void
  onMembership: () => void
  onSignOut: () => void
}) {
  const itemsRef = useRef<HTMLButtonElement[]>([])
  const registerItem = (el: HTMLButtonElement | null) => {
    if (el && !itemsRef.current.includes(el)) itemsRef.current.push(el)
  }

  // Focus the first item when the menu opens.
  useEffect(() => {
    const t = window.setTimeout(() => itemsRef.current[0]?.focus(), 0)
    return () => window.clearTimeout(t)
  }, [])

  const handleKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const items = itemsRef.current.filter(Boolean)
    if (items.length === 0) return
    const idx = items.findIndex((el) => el === document.activeElement)
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      items[(idx + 1 + items.length) % items.length]?.focus()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      items[(idx - 1 + items.length) % items.length]?.focus()
    } else if (e.key === 'Home') {
      e.preventDefault(); items[0]?.focus()
    } else if (e.key === 'End') {
      e.preventDefault(); items[items.length - 1]?.focus()
    }
  }

  // Reset refs each render so item order stays in sync.
  itemsRef.current = []

  const displayName = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.username || ''

  return (
    <div role="menu" aria-label="User menu" className="user-menu" onKeyDown={handleKey}>
      {/* Identity */}
      <div className="user-menu__header">
        <div className="user-menu__avatar">
          {avatarUrl
            ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
            : initials}
        </div>
        <div className="user-menu__identity">
          <div className="user-menu__name">{displayName}</div>
          <div className="user-menu__email">{user.email}</div>
        </div>
      </div>

      {/* Settings — one entry covers Profile / Account / Security / Preferences tabs */}
      <div className="user-menu__group" role="group" aria-label="Account">
        <MenuItem
          ref={registerItem}
          icon={Settings}
          label="Settings"
          onClick={onSettings}
          trailing={user.twoFactorEnabled ? (
            <span className="menu-chip menu-chip--success">
              <ShieldCheck size={10} /> 2FA
            </span>
          ) : undefined}
        />
      </div>

      {/* Plan */}
      <div className="user-menu__group" role="group" aria-label="Plan">
        <MenuItem
          ref={registerItem}
          icon={Crown}
          label="Membership"
          onClick={onMembership}
          trailing={tier ? <TierBadge category={tier.category} tier={tier.tier} /> : undefined}
        />
      </div>

      {/* Sign out */}
      <div className="user-menu__group" role="group" aria-label="Session">
        <MenuItem ref={registerItem} icon={LogOut} label="Sign out" onClick={onSignOut} danger />
      </div>
    </div>
  )
}

type MenuItemProps = {
  icon: typeof Shield
  label: string
  onClick: () => void
  trailing?: React.ReactNode
  danger?: boolean
}

const MenuItem = forwardRef<HTMLButtonElement, MenuItemProps>(
  function MenuItem({ icon: Icon, label, onClick, trailing, danger }, ref) {
    return (
      <button
        type="button"
        ref={ref}
        onClick={onClick}
        role="menuitem"
        className={`user-menu__item${danger ? ' user-menu__item--danger' : ''}`}
      >
        <Icon size={15} strokeWidth={1.75} className="user-menu__item-icon" />
        <span className="user-menu__item-label">{label}</span>
        {trailing ? <span className="user-menu__item-trailing">{trailing}</span> : null}
      </button>
    )
  },
)
